import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { isAxiosError } from 'axios'
import { CheckCircle2, MessageSquare, Star } from 'lucide-react'
import { Container } from '@/components/layout/Container'
import { AnimateIn } from '@/components/motion/AnimateIn'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { webApi } from '@/services/webApi'
import { cn } from '@/lib/utils'

const RATING_LABELS = ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'] as const

const schema = z.object({
  name: z.string().min(2, 'Name is required').max(80),
  comment: z
    .string()
    .min(10, 'Please write at least 10 characters')
    .max(1000, 'Please keep it under 1000 characters'),
})

type FeedbackForm = z.infer<typeof schema>

function FeedbackPanel({ embedded = false }: { embedded?: boolean }) {
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const [ratingError, setRatingError] = useState('')
  const [serverError, setServerError] = useState('')
  const nonceRef = useRef<string | null>(null)
  const honeypotRef = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FeedbackForm>({
    resolver: zodResolver(schema),
  })

  const displayRating = hoverRating || rating

  const fetchNonce = useCallback(async () => {
    try {
      const res = await webApi.feedbackNonce()
      nonceRef.current = res.nonce
    } catch {
      nonceRef.current = null
    }
  }, [])

  // Fetch a single-use submission token up front.
  useEffect(() => {
    void fetchNonce()
  }, [fetchNonce])

  const onSubmit = handleSubmit(async (values) => {
    if (rating < 1) {
      setRatingError('Please select a star rating')
      return
    }
    setRatingError('')
    setServerError('')

    if (!nonceRef.current) {
      await fetchNonce()
      if (!nonceRef.current) {
        setServerError('Could not reach the server. Please try again.')
        return
      }
    }

    try {
      await webApi.submitFeedback({
        name: values.name,
        rating,
        comment: values.comment,
        nonce: nonceRef.current,
        company: honeypotRef.current?.value || undefined,
      })
      nonceRef.current = null
      setSubmitted(true)
      reset()
      setRating(0)
      setHoverRating(0)
    } catch (err) {
      const status = isAxiosError(err) ? err.response?.status : undefined
      const message = isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : undefined

      if (status === 419) {
        setServerError('Your session refreshed — please submit once more.')
      } else if (status === 429) {
        setServerError('Too many attempts. Please wait a minute and try again.')
      } else {
        setServerError(message || 'Something went wrong. Please try again.')
      }
      // Always refresh the token so the next attempt has a valid nonce.
      await fetchNonce()
    }
  })

  const handleReset = () => {
    setSubmitted(false)
    setRating(0)
    setRatingError('')
    setServerError('')
    reset()
    void fetchNonce()
  }

  if (submitted) {
    return (
      <div className="flex h-full min-h-[300px] flex-col items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50/80 px-5 py-8 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-600" />
        <h3 className="mt-3 font-display text-lg font-bold text-neutral-900">Thank you!</h3>
        <p className="mt-1.5 text-sm text-neutral-600">We appreciate your feedback.</p>
        <Button type="button" variant="secondary" size="sm" className="mt-4" onClick={handleReset}>
          Submit another
        </Button>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex h-full flex-col rounded-2xl border border-neutral-200/90 bg-white p-4 shadow-sm sm:p-5',
        !embedded && 'lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:items-center lg:gap-8 lg:p-6',
      )}
    >
      <div className={cn(embedded ? 'mb-3' : 'lg:pr-2')}>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-700">
          <MessageSquare className="h-3 w-3" />
          We value your opinion
        </span>
        <h2 className="mt-2 font-display text-lg font-bold leading-tight text-neutral-900 sm:text-xl">
          Share Your{' '}
          <span className="bg-linear-to-r from-primary-600 to-sky-600 bg-clip-text text-transparent">
            Feedback
          </span>
        </h2>
        <p className="mt-1 text-xs leading-relaxed text-neutral-500 sm:text-sm">
          Name, rating, and a short comment — that&apos;s all we need.
        </p>
      </div>

      <form onSubmit={onSubmit} className="flex flex-1 flex-col space-y-2.5">
        {/* Honeypot — hidden from real users; bots that fill it are rejected. */}
        <input
          ref={honeypotRef}
          type="text"
          name="company"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0"
        />
        <div>
          <Label htmlFor="home-feedback-name" className="text-xs font-semibold text-neutral-800">
            Your name <span className="text-danger-500">*</span>
          </Label>
          <Input
            id="home-feedback-name"
            {...register('name')}
            placeholder="Enter your name"
            className="mt-1 h-9 rounded-lg border-neutral-200 text-sm"
          />
          {errors.name && <p className="mt-0.5 text-[11px] text-danger-500">{errors.name.message}</p>}
        </div>

        <div className="rounded-lg border border-amber-100 bg-amber-50/40 px-3 py-2">
          <Label className="text-xs font-semibold text-neutral-800">
            Rate your experience <span className="text-danger-500">*</span>
          </Label>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <div
              className="flex gap-0.5"
              role="radiogroup"
              aria-label="Star rating"
              onMouseLeave={() => setHoverRating(0)}
            >
              {Array.from({ length: 5 }).map((_, i) => {
                const value = i + 1
                const filled = value <= displayRating
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={rating === value ? 'true' : 'false'}
                    aria-label={`${value} star${value > 1 ? 's' : ''}`}
                    className="rounded p-0.5 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
                    onMouseEnter={() => setHoverRating(value)}
                    onClick={() => {
                      setRating(value)
                      setRatingError('')
                    }}
                  >
                    <Star
                      className={cn(
                        'h-6 w-6 sm:h-7 sm:w-7',
                        filled ? 'fill-amber-400 text-amber-400' : 'fill-neutral-100 text-neutral-300',
                      )}
                    />
                  </button>
                )
              })}
            </div>
            {displayRating > 0 && (
              <span className="text-xs font-semibold text-amber-800">
                {RATING_LABELS[displayRating - 1]}
              </span>
            )}
          </div>
          {ratingError && <p className="mt-0.5 text-[11px] text-danger-500">{ratingError}</p>}
        </div>

        <div className="flex-1">
          <Label htmlFor="home-feedback-comment" className="text-xs font-semibold text-neutral-800">
            Your feedback <span className="text-danger-500">*</span>
          </Label>
          <Textarea
            id="home-feedback-comment"
            {...register('comment')}
            placeholder="What did you like, or what could we improve?"
            className="mt-1 min-h-[72px] resize-none rounded-lg border-neutral-200 text-sm leading-relaxed"
            rows={2}
          />
          {errors.comment && (
            <p className="mt-0.5 text-[11px] text-danger-500">{errors.comment.message}</p>
          )}
        </div>

        {serverError && (
          <p className="rounded-lg bg-danger-50 px-3 py-2 text-[11px] font-medium text-danger-600">
            {serverError}
          </p>
        )}

        <div className="flex justify-end pt-0.5">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="h-9 rounded-lg bg-primary-600 px-5 text-sm hover:bg-primary-700"
          >
            {isSubmitting ? 'Submitting…' : 'Submit Feedback'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export function HomeFeedback({ embedded = false }: { embedded?: boolean }) {
  if (embedded) return <FeedbackPanel embedded />

  return (
    <section className="border-t border-neutral-100 bg-linear-to-b from-white to-primary-50/30 py-5 sm:py-6">
      <Container>
        <AnimateIn>
          <FeedbackPanel />
        </AnimateIn>
      </Container>
    </section>
  )
}
